import {
  DuviSharedAdminCard as Card,
  DuviSharedAdminCardHeader as CardHeader,
  DuviSharedAdminCardBody as CardBody,
  DuviSharedAdminButton as Button,
  DuviSharedAdminInput as Input,
  DuviSharedAdminSelect as Select,
  DuviSharedAdminFormField as FormField,
  DuviSharedAdminBadge as Badge,
  DuviSharedAdminIconButton as IconButton,
  DuviSharedAdminTooltip as Tooltip,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminMarkdown as Markdown,
  DuviSharedAdminTestIds,
} from "@duvi-agency/ui";
import { DuviCoreAdminLineNumberedTextarea as LineNumberedTextarea } from "../../components/duvi-core-admin-kit";
import {
  DUVI_CORE_ADMIN_KB_CATEGORIES,
  type useDuviCoreAdminKnowledgeBaseEditor,
} from "./use-duvi-core-admin-knowledge-base-editor";

type State = ReturnType<typeof useDuviCoreAdminKnowledgeBaseEditor>;

const TOOLBAR: Array<[string, string]> = [
  ["H1", "\n# "],
  ["H2", "\n## "],
  ["B", "**bold**"],
  ["I", "*italic*"],
  ["•", "\n- "],
  ["1.", "\n1. "],
  ["<>", "`code`"],
  ['"', "\n> "],
];

export function DuviCoreAdminKnowledgeBaseEditorView(state: State) {
  const {
    title,
    setTitle,
    category,
    setCategory,
    tags,
    tagInput,
    setTagInput,
    addTag,
    removeTag,
    content,
    setContent,
    appendSnippet,
    canPublish,
    isPublishing,
    saved,
    handleSaveDraft,
    handlePublish,
  } = state;

  return (
    <div
      className="flex flex-col gap-5"
      data-testid={DuviSharedAdminTestIds.kbEditorPage}
    >
      <Card pad>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="flex items-center gap-2.5 text-text-muted text-body-sm">
            <Icon name="edit" size={15} /> {saved}
          </span>
          <div className="flex gap-2.5">
            <Button
              variant="secondary"
              leftIcon={<Icon name="check" size={15} />}
              onClick={handleSaveDraft}
              data-testid={DuviSharedAdminTestIds.kbEditorSaveDraft}
            >
              Save draft
            </Button>
            <Tooltip
              content={
                canPublish
                  ? "Publish this article"
                  : "Add a title, category and content first"
              }
            >
              <Button
                disabled={!canPublish || isPublishing}
                leftIcon={<Icon name="arrowUpRight" size={15} />}
                onClick={handlePublish}
                data-testid={DuviSharedAdminTestIds.kbEditorPublish}
              >
                {isPublishing ? "Publishing…" : "Publish"}
              </Button>
            </Tooltip>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-[60%_40%] gap-5 items-start max-[900px]:grid-cols-1">
        <div className="flex flex-col gap-[18px]">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Article title"
            data-testid={DuviSharedAdminTestIds.kbEditorTitle}
            className="border-none bg-transparent outline-none text-h2 font-bold tracking-tight text-text-primary w-full"
          />
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Category" required>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Select a category"
                data-testid={DuviSharedAdminTestIds.kbEditorCategory}
              >
                {DUVI_CORE_ADMIN_KB_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Tags" help="Press Enter to add">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={addTag}
                placeholder="Add a tag"
                data-testid={DuviSharedAdminTestIds.kbEditorTagInput}
              />
            </FormField>
          </div>
          {tags.length > 0 && (
            <div className="flex gap-1.5 flex-wrap -mt-1.5">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 text-caption font-semibold px-[9px] py-[3px] rounded-full bg-primary-subtle text-primary"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    className="border-none bg-transparent cursor-pointer text-inherit inline-flex p-0 ml-0.5"
                    aria-label={`Remove ${t}`}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <FormField label="Content" required>
            <div className="flex gap-1 mb-2 flex-wrap">
              {TOOLBAR.map(([label, snippet]) => (
                <IconButton
                  key={label}
                  label={`Insert ${label}`}
                  size="sm"
                  onClick={() => appendSnippet(snippet)}
                >
                  <span className="font-mono text-caption font-bold">
                    {label}
                  </span>
                </IconButton>
              ))}
            </div>
            <LineNumberedTextarea
              id="markdown-editor"
              value={content}
              onChange={setContent}
              placeholder="Write in Markdown…"
            />
          </FormField>
        </div>

        <div className="sticky top-[88px]">
          <Card>
            <CardHeader
              title="Live preview"
              icon={<Icon name="eye" size={18} />}
              actions={
                category ? <Badge variant="primary">{category}</Badge> : null
              }
            />
            <CardBody>
              <Markdown
                content={content}
                className="text-body-sm text-text-primary"
              />
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
